// src/data/services.ts
export interface Service {
  id: string;
  name: string;
  category: 'nails' | 'eyelashes' | 'makeup' | 'packages' | 'addons';
  price: number; // Price in Ghana Cedis (₵)
  duration: string;
  description: string;
  popular: boolean;
  features: string[];
  ghanaianNote?: string;
  includes?: string[];
  bestFor?: string[];
}

export const services: Service[] = [
  // NAIL SERVICES
  {
    id: 'signature-manicure',
    name: 'Signature Ghanaian Manicure',
    category: 'nails',
    price: 120,
    duration: '1 hour',
    description: 'Premium manicure using locally-sourced shea butter and aloe vera for maximum hydration. Perfect for Accra\'s climate.',
    popular: true,
    features: [
      'Nail shaping & professional buffing',
      'Cuticle treatment with shea butter',
      'Traditional Ghanaian hand massage',
      'Hydrating mask with aloe vera',
      'Choice of regular polish'
    ],
    ghanaianNote: 'Uses authentic Ghanaian shea butter from the North',
    bestFor: ['Daily wear', 'Office professionals', 'Maintenance']
  },
  {
    id: 'gel-manicure',
    name: 'Gel Polish Manicure',
    category: 'nails',
    price: 180,
    duration: '75 min',
    description: 'Long-lasting gel polish that withstands Accra\'s humidity and lasts 2-3 weeks.',
    popular: true,
    features: [
      'Nail prep & sanitization',
      'Gel polish application',
      'UV/LED curing',
      'Top coat seal',
      'Cuticle oil with baobab oil'
    ],
    bestFor: ['Long-lasting wear', 'Busy professionals', 'Special occasions']
  },
  {
    id: 'african-spa-pedicure',
    name: 'African Spa Pedicure',
    category: 'nails',
    price: 150,
    duration: '1 hour 15 min',
    description: 'Luxurious pedicure featuring traditional foot treatments with natural Ghanaian ingredients.',
    popular: true,
    features: [
      'Warm soak with sea salt & neem leaves',
      'Callus removal & smoothing',
      'Exfoliation with coconut scrub',
      'Traditional foot massage',
      'Paraffin wax treatment'
    ],
    ghanaianNote: 'Features local neem leaves from Ghanaian gardens'
  },
  {
    id: 'adinkra-nail-art',
    name: 'Adinkra Nail Art',
    category: 'nails',
    price: 200,
    duration: '90 min',
    description: 'Traditional Adinkra symbols or modern Afro-chic designs hand-painted by our expert artists.',
    popular: true,
    features: [
      'One-on-one design consultation',
      'Hand-painted Adinkra symbols',
      'Kente-inspired geometric patterns',
      'Accra skyline designs available',
      'Gold foil & glitter accents'
    ],
    ghanaianNote: 'Traditional Adinkra symbol meanings explained',
    bestFor: ['Traditional weddings', 'Cultural events', 'Special celebrations']
  },
  {
    id: 'nail-extensions',
    name: 'Acrylic/Gel Extensions',
    category: 'nails',
    price: 250,
    duration: '2 hours',
    description: 'Professional nail extensions using high-quality acrylic or gel for strong, beautiful nails.',
    popular: false,
    features: [
      'Custom nail shaping',
      'Acrylic or gel application',
      'Length customization',
      'Filing & buffing',
      'Polish of choice included'
    ],
    includes: ['Free fill-in after 2 weeks']
  },

  // EYE LASH SERVICES
  {
    id: 'classic-lashes',
    name: 'Classic Lash Extensions',
    category: 'eyelashes',
    price: 300,
    duration: '2 hours',
    description: 'Natural-looking lash extensions that enhance your eyes for everyday elegance.',
    popular: true,
    features: [
      'Individual lash application',
      'Custom length & curl selection',
      'Hypoallergenic adhesive',
      'Eye protection pads',
      'Aftercare consultation'
    ],
    bestFor: ['Natural look', 'Office wear', 'Daily enhancement']
  },
  {
    id: 'volume-lashes',
    name: 'Volume Lash Extensions',
    category: 'eyelashes',
    price: 400,
    duration: '2.5 hours',
    description: 'Full, dramatic lash look perfect for special occasions and events in Accra.',
    popular: true,
    features: [
      '2D-6D volume fans',
      'Lightweight Russian volume',
      'Custom density selection',
      'Premium synthetic lashes',
      'Full eye mapping'
    ],
    bestFor: ['Weddings', 'Parties', 'Special events', 'Photoshoots']
  },
  {
    id: 'lash-lift-tint',
    name: 'Lash Lift & Tint',
    category: 'eyelashes',
    price: 180,
    duration: '1 hour',
    description: 'Lift and tint your natural lashes for a curled, dark look without extensions.',
    popular: false,
    features: [
      'Lash perming & lifting',
      'Professional tinting',
      'Keratin treatment',
      'No extensions needed',
      'Lasts 6-8 weeks'
    ],
    bestFor: ['Natural enhancement', 'Low maintenance', 'Sensitive eyes']
  },
  {
    id: 'lash-fill',
    name: 'Lash Fill/Refill',
    category: 'eyelashes',
    price: 180,
    duration: '1.5 hours',
    description: 'Maintenance service to keep your lash extensions looking full and beautiful.',
    popular: false,
    features: [
      'Remove outgrown lashes',
      'Apply new extensions',
      'Clean & sanitize',
      'Reshape if needed',
      'Polish & perfect'
    ]
  },

  // MAKE-UP SERVICES
  {
    id: 'natural-makeup',
    name: 'Natural Day Makeup',
    category: 'makeup',
    price: 250,
    duration: '1 hour',
    description: 'Light, natural makeup perfect for Accra\'s daytime events and professional settings.',
    popular: true,
    features: [
      'Skin prep & moisturizing',
      'Light foundation application',
      'Natural eye enhancement',
      'Subtle contour & highlight',
      'Matte or dewy finish option'
    ],
    includes: ['False lashes optional (+₵50)'],
    bestFor: ['Office events', 'Lunch dates', 'Daytime functions']
  },
  {
    id: 'glam-makeup',
    name: 'Full Glam Makeup',
    category: 'makeup',
    price: 350,
    duration: '1.5 hours',
    description: 'Full coverage glamorous makeup for weddings, parties, and special occasions.',
    popular: true,
    features: [
      'Full face makeup',
      'Cut crease or smoky eye',
      'Contour & dramatic highlight',
      'False lash application',
      'Setting spray for longevity'
    ],
    includes: ['False lashes included', 'Touch-up kit'],
    bestFor: ['Weddings', 'Evening events', 'Photoshoots', 'Parties']
  },
  {
    id: 'bridal-makeup',
    name: 'Bridal Makeup Package',
    category: 'makeup',
    price: 500,
    duration: '2 hours',
    description: 'Complete bridal makeup experience including trial and wedding day service.',
    popular: true,
    features: [
      'Pre-wedding consultation',
      'Trial session included',
      'Wedding day makeup',
      'Touch-up kit provided',
      'Lasts all day & night'
    ],
    includes: ['Trial session', 'False lashes', 'Bride\'s touch-up kit']
  },
  {
    id: 'traditional-makeup',
    name: 'Traditional Ghanaian Makeup',
    category: 'makeup',
    price: 300,
    duration: '1.5 hours',
    description: 'Traditional makeup styles for cultural ceremonies, festivals, and traditional weddings.',
    popular: false,
    features: [
      'Traditional face designs',
      'Cultural appropriate styles',
      'Long-wearing formulas',
      'Photography-ready finish',
      'Cultural consultation'
    ],
    ghanaianNote: 'Respects cultural traditions and meanings',
    bestFor: ['Traditional weddings', 'Cultural festivals', 'Naming ceremonies']
  },

  // PACKAGES & ADDONS
  {
    id: 'bridal-package',
    name: 'Complete Bridal Package',
    category: 'packages',
    price: 1200,
    duration: '4 hours',
    description: 'Complete bridal beauty experience including bride and 2 bridesmaids.',
    popular: true,
    features: [
      'Bridal makeup & lashes',
      'Bride\'s manicure & pedicure',
      '2 bridesmaids\' makeup',
      'Pre-wedding consultation',
      'Emergency touch-up kit'
    ],
    includes: ['Bride + 2 bridesmaids', 'All-day support', 'Gift package']
  },
  {
    id: 'corporate-package',
    name: 'Corporate Beauty Package',
    category: 'packages',
    price: 800,
    duration: 'Custom',
    description: 'Office or event beauty services for corporate clients in Greater Accra.',
    popular: false,
    features: [
      'On-site service available',
      'Group discounts (5+ people)',
      'Professional nail care',
      'Express makeup options',
      'Custom scheduling'
    ],
    includes: ['Mobile service available', 'Bulk booking discount']
  },
  {
    id: 'event-package',
    name: 'Event Beauty Package',
    category: 'packages',
    price: 600,
    duration: '3 hours',
    description: 'Group beauty services for parties, graduations, and special events.',
    popular: false,
    features: [
      'Makeup for 3 people',
      'Manicures for 3 people',
      'Quick lash options',
      'Group styling',
      'Event-ready in 3 hours'
    ]
  },
  {
    id: 'maintenance-package',
    name: 'Monthly Maintenance Package',
    category: 'packages',
    price: 900,
    duration: 'Monthly',
    description: 'Monthly beauty maintenance package for regular clients.',
    popular: true,
    features: [
      '2 gel manicures monthly',
      '1 lash fill monthly',
      '1 facial treatment',
      '10% off other services',
      'Priority booking'
    ],
    includes: ['Save 20% vs individual services']
  }
];

export const serviceCategories = [
  { id: 'nails', name: 'Nail Services', icon: 'Hand', color: 'bg-pink-100 text-pink-800' },
  { id: 'eyelashes', name: 'Eyelash Services', icon: 'Eye', color: 'bg-purple-100 text-purple-800' },
  { id: 'makeup', name: 'Makeup Services', icon: 'Palette', color: 'bg-rose-100 text-rose-800' },
  { id: 'packages', name: 'Beauty Packages', icon: 'Package', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'addons', name: 'Add-ons & Extras', icon: 'Plus', color: 'bg-amber-100 text-amber-800' },
];

